import React, { useState, useEffect, useContext } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import styles from "./team-management.module.css";
import instance from "services/api";
import { useParams } from "react-router";
import Disclaimer from "components/functional/disclaimer";
import { formatDate } from "helpers/date";
import ToggleActions from "components/functional/toggle-actions";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { MembershipContext } from "hoc/membership-data";
import { permissionsService } from "services/permissions.service";
import { ToastService, ToastComponent } from "services/toast.service";
import tokenService from "services/token.service";
import EmptyTeam from "components/empty-state/teams/empty-team";
import Spinner from "components/functional/spinner";
import { NavLink } from "react-router";
import useIsMobile from "hooks/detect-mobile-screen";
import {
  CalendarClock,
  MoveLeft,
  Plus,
  Settings,
  EllipsisVertical,
  UserPlus,
  Trash2,
} from "lucide-react";

const TeamManagement = () => {
  const [teamData, setTeamData] = useState([]);
  const navigate = useNavigate();
  const { team_id } = useParams();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const membershipData = useContext(MembershipContext);
  const { teams } = membershipData;
  const team = teams.find((team) => team.team_id === team_id);

  const teamManagementLink = {
    manage_preferences: `/teams/${team_id}/preferences`,
  };

  const teamActions = [
    {
      permission: "manage_preferences",
      label: t("teams.card.actions.manage_preferences"),
      icon: <CalendarClock size={16} className="icon icon-secondary" />,
      command: () => navigate(teamManagementLink.manage_preferences),
    },
    !team.is_player && {
      permission: "add_self_to_team",
      label: t("teams.card.actions.add_myself_to_team"),
      icon: <UserPlus size={16} className="icon icon-secondary" />,
      command: () => addAdminToTeam(),
    },
  ].filter((action) =>
    permissionsService.hasPermission(action.permission, team_id)
  );

  const addAdminToTeam = async () => {
    try {
      await instance.post(`/team-players/add-admin`, {
        team_id,
        user_id: tokenService.getUserId(),
        is_captain: true,
      });

      ToastService.showSuccessToast("Admin ajouté à l'équipe");

      window.location.reload();
    } catch (error) {
      console.error(error);
    }
  };

  const fetchTeam = async (loading = true) => {
    try {
      const response = await instance.get(`/teams/${team_id}/data`);
      setTeamData(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchTeam();
  }, []);

  const nameTemplate = (player) => {
    return (
      <div className={styles["name-wrapper"]}>
        <div className={styles.icon}>
          <p className="title-4">
            {player.first_name[0]}
            {player.last_name[0]}
          </p>
        </div>
        <div className={styles["name-infos"]}>
          <p className="title-3-variant-low">
            {player.first_name} {player.last_name}
          </p>
          <p className="subtitle-2-variant">{player.email}</p>
        </div>
      </div>
    );
  };

  const roleTemplate = (player) => {
    const role = player.is_captain ? "captain" : "player";
    return <Disclaimer status={role} />;
  };

  const statusTemplate = (player) => {
    const status = player.is_archived
      ? t("teams.management.players.status.archived")
      : t("teams.management.players.status.active");
    return <p className="title-3-variant-low">{status}</p>;
  };

  const dateTemplate = (player) => {
    const formattedDate = formatDate(player.created_at, "DD MMMM YYYY");
    return <p className="title-3-variant-low">{formattedDate}</p>;
  };

  const archivePlayer = async (player) => {
    try {
      await instance.post(`/team-players/archive`, {
        team_id,
        player_id: player.player_id,
      });

      ToastService.showSuccessToast(t("teams.management.archived-player"));

      await fetchTeam(false);
    } catch (error) {
      console.error(error);
    }
  };

  const activatePlayer = async (player) => {
    try {
      await instance.post(`/team-players/activate`, {
        team_id,
        player_id: player.player_id,
      });

      ToastService.showSuccessToast(t("teams.management.active-player"));

      await fetchTeam(false);
    } catch (error) {
      console.error(error);
    }
  };

  const actionsTemplate = (player) => {
    const is_allowed = player.is_captain
      ? permissionsService.hasPermission("remove_captain", team_id)
      : permissionsService.hasPermission("remove_player", team_id);

    const is_current_player = team.player_id === player.player_id;

    const actions = [
      player.is_archived
        ? {
            permission: player.is_captain
              ? "activate_captain"
              : "activate_player",
            label: player.is_captain ? "Activate captain" : "Activate player",
            icon: <Trash2 size={16} className="icon icon-secondary" />,
            command: () => activatePlayer(player),
          }
        : {
            permission: player.is_captain ? "remove_captain" : "remove_player",
            label: player.is_captain ? "Remove captain" : "Remove player",
            icon: <Trash2 size={16} className="icon icon-secondary" />,
            command: () => archivePlayer(player),
          },
    ].filter((action) =>
      permissionsService.hasPermission(action.permission, team_id)
    );

    return is_allowed && !is_current_player ? (
      <ToggleActions
        actions={actions}
        btn={{
          class: "btn--transparent",
          icon: <EllipsisVertical size={20} className="icon icon-secondary" />,
        }}
      />
    ) : (
      <></>
    );
  };

  const handleInvitations = () => {
    navigate(`/teams/${team_id}/invite/`);
  };

  if (!teamData.players || !teamData.team) {
    return <Spinner />;
  }

  return (
    <div className="dashboard-container">
      <ToastComponent />
      <header className={`${styles.header} mb-32`}>
        <div className={styles["header-wrapper"]}>
          <div className={styles.title}>
            <div className={styles["title-wrapper"]}>
              <NavLink className="btn btn--transparent" to={-1}>
                <MoveLeft size={20} />
              </NavLink>
              <h1 className="title-1">{t("teams.management.title")}</h1>
            </div>
          </div>
          <div className={styles.actions}>
            <ToggleActions
              actions={teamActions}
              btn={{
                class: "btn--secondary",
                icon: <Settings size={20} className="icon icon-secondary" />,
              }}
            />
            {isMobile ? (
              <button
                className="btn btn--primary btn--icon-only"
                onClick={handleInvitations}
              >
                <Plus size={20} />
              </button>
            ) : (
              <button className="btn btn--primary" onClick={handleInvitations}>
                {t("teams.management.actions.invite-players")}
              </button>
            )}
          </div>
        </div>
        <p className="subtitle-2-variant">{teamData.team.name}</p>
      </header>
      {teamData.players?.length > 0 ? (
        <section className={styles.wrapper}>
          <h3 className="title-3">
            {t("teams.management.players.title")} ({teamData.players?.length})
          </h3>
          <DataTable value={teamData.players}>
            <Column
              header={t("teams.management.players.table.name")}
              body={nameTemplate}
            ></Column>
            <Column
              header={t("teams.management.players.table.role")}
              body={roleTemplate}
            ></Column>
            {!isMobile && (
              <Column
                header={t("teams.management.players.table.status")}
                body={statusTemplate}
              ></Column>
            )}
            {!isMobile && (
              <Column
                header={t("teams.management.players.table.added-at")}
                body={dateTemplate}
              ></Column>
            )}
            <Column
              header=""
              body={actionsTemplate}
              className={styles["actions-column"]}
            ></Column>
          </DataTable>
        </section>
      ) : (
        <div className={styles.empty}>
          <EmptyTeam handleInvitations={handleInvitations}></EmptyTeam>
        </div>
      )}
    </div>
  );
};

export default TeamManagement;
